import React from "react";
import { Heart } from "lucide-react";
import { Reveal, PETROL } from "@/components/home/homeUi";
import { seatStatus } from "./seatStatus";

const SESSION_LABELS = ["Időpont", "Időtartam", "Részvételi díj"];
const huf = (n) => `${Number(n).toLocaleString("hu-HU").replace(/\s/g, ".")} Ft/fő`;
const BADGE_TONES = {
  low: "text-gold border-[rgba(160,122,40,0.45)] bg-[rgba(201,169,97,0.08)]",
  full: "text-gold border-[rgba(160,122,40,0.3)]",
  closed: "text-muted-warm border-[#E6E1D8]",
};

function SeatBadge({ session }) {
  const st = seatStatus(session);
  if (!st) return null;
  return (
    <span data-testid={`group-program-seat-status-${session.id}`} data-tone={st.tone} className={`inline-block font-body text-[13px] md:text-[13.5px] tracking-[0.03em] px-3.5 py-1.5 border ${BADGE_TONES[st.tone]}`}>
      {st.text}
    </span>
  );
}

// With admin sessions, date/duration/price come from the session; other rows stay as written.
function cardInfo(program) {
  const sessions = program.sessions || [];
  if (sessions.length === 0) return program.info;
  const rest = program.info.filter((r) => !SESSION_LABELS.includes(r.label) && r.value);
  if (sessions.length > 1) return rest;
  const s = sessions[0];
  const fromSession = {
    "Időpont": `${s.date_label}, ${s.time}`,
    "Időtartam": `${s.duration_minutes} perc`,
    "Részvételi díj": huf(s.price_huf),
  };
  const rows = program.info.map((r) => (r.label in fromSession ? { ...r, value: fromSession[r.label] } : r)).filter((r) => r.value);
  SESSION_LABELS.forEach((l) => { if (!rows.some((r) => r.label === l)) rows.push({ label: l, value: fromSession[l] }); });
  return rows;
}

function SessionList({ sessions }) {
  return (
    <ul data-testid="group-program-sessions">
      {sessions.map((s) => (
        <li key={s.id} data-testid={`group-program-session-${s.id}`} className="py-4 border-b flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2" style={{ borderColor: `${PETROL}26` }}>
          <div>
            <p className="font-body text-[10.5px] md:text-[11px] tracking-[0.3em] uppercase mb-1" style={{ color: PETROL }}>Időpont</p>
            <p className="font-body text-[15px] md:text-[15.5px] text-graphite">{`${s.date_label}, ${s.time}`}</p>
            <p className="font-body text-[13.5px] text-graphite-soft">{`${s.duration_minutes} perc · ${huf(s.price_huf)}`}</p>
          </div>
          <SeatBadge session={s} />
        </li>
      ))}
    </ul>
  );
}

function ProgramInfo({ info }) {
  return (
    <dl className="mt-9 grid grid-cols-1 sm:grid-cols-2 border-t" style={{ borderColor: `${PETROL}26` }}>
      {info.map((row, i) => (
        <div
          key={row.label}
          className={`py-4 sm:px-5 border-b ${i % 2 === 1 ? "sm:border-l" : "sm:pl-0"} ${
            i === info.length - 1 && i % 2 === 0 ? "sm:col-span-2" : ""
          }`}
          style={{ borderColor: `${PETROL}26` }}
        >
          <dt className="font-body text-[10.5px] md:text-[11px] tracking-[0.3em] uppercase mb-1.5" style={{ color: PETROL }}>
            {row.label}
          </dt>
          <dd className="font-body text-[15px] md:text-[15.5px] leading-[1.6] text-graphite whitespace-pre-line">{row.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export default function GroupProgramCard({ program, onApply }) {
  const soon = program.status === "soon";
  const last = program.description.length - 1;
  const sessions = program.sessions || [];
  const canApply = sessions.some((s) => s.open);
  const canWaitlist = !canApply && sessions.some((s) => s.waitlist_open);
  const active = canApply || canWaitlist;
  return (
    <Reveal>
      <article
        data-testid={`group-program-card-${program.id}`}
        className={`relative border px-6 sm:px-9 md:px-12 py-10 md:py-12 ${soon ? "bg-tinted" : "bg-white"}`}
        style={{
          borderColor: soon ? "var(--border-hairline, #E6E1D8)" : "rgba(160,122,40,0.35)",
          boxShadow: soon ? "none" : "0 18px 50px -38px rgba(160,122,40,0.45)",
        }}
      >
        <span aria-hidden="true" className="absolute top-0 left-6 sm:left-9 md:left-12 h-[2px] w-14" style={{ background: "var(--accent-gold)", opacity: soon ? 0.4 : 0.85 }} />
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p data-testid={`group-program-number-${program.id}`} className="font-body text-[11px] md:text-[12px] tracking-[0.34em] uppercase text-gold">
            {program.number}
          </p>
          {soon && (
            <span data-testid={`group-program-soon-${program.id}`} className="font-body text-[10.5px] tracking-[0.3em] uppercase px-3.5 py-1.5 border" style={{ color: PETROL, borderColor: `${PETROL}55` }}>
              Hamarosan
            </span>
          )}
        </div>
        <h3
          data-testid={`group-program-title-${program.id}`}
          className={`mt-5 font-serif-display tracking-[0.06em] leading-[1.15] ${soon ? "text-graphite-soft text-[26px] md:text-[32px]" : "text-graphite text-[28px] sm:text-[34px] md:text-[40px]"}`}
        >
          {program.title}
        </h3>
        {program.subtitle && (
          <p className="mt-4 font-serif-display italic text-gold text-[19px] md:text-[22px] leading-[1.5]">{program.subtitle}</p>
        )}
        <div className="mt-7 space-y-5 reading-prose font-body font-light text-graphite text-[16px] md:text-[17px] leading-[1.85]">
          {program.description.map((t, i) => (
            <p key={t.slice(0, 24)}>
              {t}
              {program.heart_after_last && i === last && (
                <Heart className="inline-block ml-1.5 -mt-1 w-4 h-4 text-gold" strokeWidth={1.4} fill="currentColor" fillOpacity={0.25} aria-label="szív" />
              )}
            </p>
          ))}
        </div>
        {program.info.length > 0 && <ProgramInfo info={cardInfo(program)} />}
        {sessions.length > 1 && <SessionList sessions={sessions} />}
        {!soon && sessions.length === 1 && seatStatus(sessions[0]) && (
          <div className="mt-8">
            <SeatBadge session={sessions[0]} />
          </div>
        )}
        {!soon && (
          <div className="mt-10">
            <button
              type="button"
              onClick={() => active && onApply(program.id)}
              disabled={!active}
              data-testid={`group-program-apply-${program.id}`}
              className="cta-graphite inline-flex items-center justify-center gap-3 px-9 py-4 text-[12px] md:text-[13px] uppercase font-body font-medium tracking-[0.22em] w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {canWaitlist ? "Várólistára jelentkezem" : "Jelentkezem az alkalomra"} <span aria-hidden="true">→</span>
            </button>
            {!active && sessions.length === 0 && (
              <p data-testid={`group-program-closed-note-${program.id}`} className="mt-3 font-body font-light text-[13px] text-muted-warm">
                Az online jelentkezés hamarosan megnyílik.
              </p>
            )}
          </div>
        )}
      </article>
    </Reveal>
  );
}
